import { useState, useEffect, useRef, useMemo } from "react";
import {
  ChatContainer as ChatContainerLibrary,
  MessageList,
  Message,
  Avatar,
} from "@chatscope/chat-ui-kit-react";
import {
  DRAWER_CAMPAIGN,
  FONT_FAMILY,
  MAP_CHAT_FROM,
  OWNER_ROLE,
} from "../../utils/constants";
import ChatCustomMessage from "../Chat/ChatCustomMessage";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  getIsBlockedActions,
  getNameBusinessTheme,
} from "../../actions/getters";
import { useCreditActionCosts } from "../../hooks/useCreditActionCosts";
import { getPathDatabaseByRole } from "../../actions/auth";
import CommonChatMessageInput from "./CommonChatMessageInput";
import { getKeyDatabase } from "../../services/public";
import {
  startGenerateQuickReplies,
  startSendGlobalAgentMessage,
  startSendMessageChatBot,
  startSendMessageOwnerHelpChatAgent,
  startSaveCampaignChat,
} from "../../actions/chat";
import { useTheme } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import MinimizeIcon from "@mui/icons-material/Minimize";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import ChatWelcomeDescription from "./ChatWelcomeDescription";
import { useCampaignOpeningAssist } from "../../pages/NewAdsCampaign/context/CampaignOpeningAssistContext";
import {
  getOpeningAssistEmptyPlaceholderMessageKeys,
  OPENING_ASSIST_EMPTY_PLACEHOLDER,
  OPENING_ASSIST_SYNTHETIC_MESSAGE_ID,
} from "../Chat/openingAssistConstants";
import { resetChatReasoningLoaderProgress } from "../Chat/ChatReasoningIndicator";

import { BOX_SHADOW_CHAT, INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  CHAT_BOT_DESCRIPTION,
  CHAT_BOT_NAME,
  CHAT_CAMPAIGN_OPEN_ANALYZING_SUGGESTIONS,
  CHAT_THINKING,
  ERROR_OCCURRED,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { Lightbulb } from "lucide-react";
import _ from "lodash";
import {
  startDeleteCampaignReport,
  startGetAdsCampaignsSchemas,
  startSetOptimizationIsApplied,
  startSetOptimizationIsDiscarded,
} from "../../actions/adsCampaigns";
import { useLocation } from "react-router-dom";
import { openLink } from "../../utils/string";
import { resolveChatAgentErrorMessage } from "../../utils/errors";
import QuickRepliesChips from "./QuickRepliesChips";
import { formatMessagesForChatAgent } from "./utils/formatMessagesForChatAgent";

const SX_CHAT_MESSAGES_FLEX_FILL = { flex: 1, minHeight: 0 };

const SX_CHAT_MESSAGES_ROOT_OVERLAY = {
  ...SX_CHAT_MESSAGES_FLEX_FILL,
  position: "relative",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const SX_CHAT_MESSAGES_ROOT_LEGACY = {
  flex: 1,
  overflow: "auto",
  height: "100%",
  pt: 0.8,
};

const SX_CHAT_MESSAGES_SCROLL_OVERLAY = {
  ...SX_CHAT_MESSAGES_FLEX_FILL,
  overflow: "auto",
  pt: 0.8,
};

const SX_CHAT_MESSAGES_SCROLL_LEGACY = { display: "contents" };

const SX_CHAT_QUICK_REPLIES_OVERLAY = {
  position: "absolute",
  left: 15,
  right: 15,
  bottom: 0,
  zIndex: 2,
  pointerEvents: "none",
};

const CHAT_LIBRARY_INLINE_STYLE = {
  border: "none",
  backgroundColor: "transparent",
  height: "100%",
  boxShadow: "none",
};

const MESSAGE_LIST_BASE_STYLE = {
  fontFamily: FONT_FAMILY,
  height: "100%",
};

const CommonChatContainer = ({
  showSendFileButton,
  chatConfigurationID,
  chatContext,
  globalReportContext,
  agentFlow,
  context = null,
  placeholderInput,
  showAvatar = false,
  onChangeShowChat = () => {},
  statisticsView,
  onCallbackChangeOptimizations = () => {},
  onRegisterClearPreloadedMessages = () => {},
  embedded = false,
  // hideLauncher = false,
  showOnlyMessages = false,
  showPreviewOptimization = true,
  controlledOpen,
  onChangeOpen,
  exposeSendMessage,
  exposeChatActions,
  exposeMessageCount,
  onAgentReply,
  preloadedMessages = {},
  sx = {},
  contentSx = {},
  showMinimize = true,
  showHeader = true,
  welcomeCenteredSingleColumn = false,
  optimizationsSingleColumn = false,
  demoMode = false,
  demoBotReply,
  disabledInteractions = false,
  messageVariant,
}) => {
  const [isTyping, setIsTyping] = useState(false);
  const [messages, setMessages] = useState({});
  const [quickReplies, setQuickReplies] = useState([]);
  const [isLoadingQuickReplies, setIsLoadingQuickReplies] = useState(false);
  const [open, setOpen] = useState(false);
  const reasoningMessageKeyRef = useRef(null);
  const theme = useTheme();

  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_TEXT_ACTION } = useCreditActionCosts();
  const byokPaymentMode = useSelectorApp(
    (state) =>
      state.payments?.byokConfigurationPublic?.defaultPaymentMode || "credits"
  );
  const location = useLocation();
  const LOGO_BUSINESS = theme?.logoChat;

  const isInStatisticsPage = location.pathname === "/ads-campaign-statistics";
  const isDashboardRoute = location.pathname === "/dashboard";
  const isGlobalReportFlow = agentFlow === "globalReport";
  /** Quick replies strip + overlay layout; disabled on dashboard, statistics, and global report. */
  const showQuickRepliesChatOverlay =
    !isDashboardRoute && !isInStatisticsPage && !isGlobalReportFlow;
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { userID } = dispatch(getPathDatabaseByRole());
  const { t } = useTranslationApp();
  const { emptyChatPlaceholder } = useCampaignOpeningAssist();
  const openingAssistPlaceholderMessageKeys =
    getOpeningAssistEmptyPlaceholderMessageKeys(emptyChatPlaceholder);
  const messagesKeys = Object.keys(messages);
  const hasRealMessages = messagesKeys.length > 0;

  const messagesForDisplay = useMemo(() => {
    if (hasRealMessages || !openingAssistPlaceholderMessageKeys) {
      return messages;
    }

    return {
      [OPENING_ASSIST_SYNTHETIC_MESSAGE_ID]: {
        message: "",
        messageType: "system",
        userID: "bot",
        isReasoning: true,
        reasoningMessageKeys: openingAssistPlaceholderMessageKeys,
        creationTime: 0,
      },
    };
  }, [hasRealMessages, openingAssistPlaceholderMessageKeys, messages]);

  const isWaitingForBotResponse =
    Object.values(messages).some((message) => message?.isReasoning) ||
    Boolean(openingAssistPlaceholderMessageKeys && !hasRealMessages);

  const displayMessageKeys = Object.keys(messagesForDisplay);
  const sortedDisplayMessages = displayMessageKeys.sort(
    (a, b) =>
      messagesForDisplay[a].creationTime - messagesForDisplay[b].creationTime
  );
  const displayMessageIndexById = useMemo(
    () => new Map(sortedDisplayMessages.map((id, index) => [id, index])),
    [sortedDisplayMessages]
  );
  const hasAnyMessages = sortedDisplayMessages.length > 0;

  useEffect(() => {
    if (emptyChatPlaceholder === OPENING_ASSIST_EMPTY_PLACEHOLDER.ANALYZING) {
      resetChatReasoningLoaderProgress();
    }
  }, [emptyChatPlaceholder]);

  useEffect(() => {
    if (!isWaitingForBotResponse) {
      resetChatReasoningLoaderProgress();
    }
  }, [isWaitingForBotResponse]);

  const sortedMessages = messagesKeys.sort(
    (a, b) => messages[a].creationTime - messages[b].creationTime
  );

  const messageListStyle = showQuickRepliesChatOverlay
    ? MESSAGE_LIST_BASE_STYLE
    : {
        ...MESSAGE_LIST_BASE_STYLE,
        marginTop: 0,
        overflow: "auto",
        background: "transparent",
      };

  const chatMessagesRootSx = showQuickRepliesChatOverlay
    ? SX_CHAT_MESSAGES_ROOT_OVERLAY
    : SX_CHAT_MESSAGES_ROOT_LEGACY;

  const chatMessagesScrollSx = showQuickRepliesChatOverlay
    ? SX_CHAT_MESSAGES_SCROLL_OVERLAY
    : SX_CHAT_MESSAGES_SCROLL_LEGACY;

  const allCurrentChatData = {
    name: `${NAME_BUSINESS} Bot`,
    userID: "bot",
    imageURL: LOGO_BUSINESS,
  };

  // Function to open campaign with chat context
  const openCampaignWithChat = (campaignID, chatID) => {
    if (!campaignID || !chatID) {
      console.error("❌ Cannot open campaign: missing campaignID or chatID");
      return null;
    }

    const url = `/ads-campaign?campaignID=${campaignID}&chatID=${chatID}`;
    openLink(url);
    return url;
  };

  const onChangeStatusOptimization = async ({
    optimization,
    messageIndex,
    status,
  }) => {
    if (disabledInteractions) {
      return;
    }

    const isDiscarded = status === "discarded";

    if (isInStatisticsPage && !isDiscarded) {
      const messageKey = sortedMessages[messageIndex];
      if (messageKey && messages[messageKey]) {
        try {
          const saveResponse = await dispatch(
            startSaveCampaignChat({
              messages,
              campaignID: chatContext?.campaign?.id,
            })
          );

          if (saveResponse.ok) {
            openCampaignWithChat(
              chatContext?.campaign?.id,
              saveResponse.chatID
            );
          }
        } catch (error) {
          console.error("❌ Error saving chat:", error);
        }
      }
      return;
    }

    const messageKey = sortedMessages[messageIndex];
    if (messageKey && messages[messageKey]) {
      setMessages((prevMessages) => {
        const updatedOptimizations = (
          prevMessages[messageKey].optimizations || []
        ).map((opt) =>
          opt.id === optimization.id
            ? {
                ...opt,
                isApplied: status === "applied",
                isDiscarded: status === "discarded",
              }
            : opt
        );

        return {
          ...prevMessages,
          [messageKey]: {
            ...prevMessages[messageKey],
            optimizations: updatedOptimizations,
          },
        };
      });
    }

    //Verify if the optimization is in the platform optimizations (auto generated)
    const platform = optimization.platform;
    const platformOptimizations =
      chatContext?.campaign?.report?.states?.[platform]?.optimizations || [];
    if (!_.isEmpty(platformOptimizations)) {
      const optimizationIndex = platformOptimizations.findIndex(
        (opt) => opt.id === optimization.id
      );
      if (optimizationIndex !== -1) {
        if (status === "applied") {
          dispatch(
            startSetOptimizationIsApplied({
              campaignID: chatContext?.campaign?.id,
              platform,
              optimizationIndex,
            })
          );
        } else {
          dispatch(
            startSetOptimizationIsDiscarded({
              campaignID: chatContext?.campaign?.id,
              platform,
              optimizationIndex,
            })
          );
        }
      }
    }

    if (!embedded) {
      handleClose();
    }
  };

  const onSendMessage = async (message) => {
    if (disabledInteractions || isTyping) {
      return false;
    }

    if (!message || typeof message !== "string" || !message.trim()) {
      return false;
    }

    if (!demoMode && context !== OWNER_ROLE) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_TEXT_ACTION,
          showUsageConfirmationAlert: true,
        })
      );
      if (isBlocked) {
        return false;
      }
    }

    setQuickReplies([]);
    setIsLoadingQuickReplies(false);
    const newMessage = {
      message,
      messageType: "text",
      userID,
      creationTime: new Date().getTime(),
    };
    const key = dispatch(getKeyDatabase());

    setMessages((prevMessages) => {
      const nextMessages = {
        ...prevMessages,
        [key]: newMessage,
      };

      const reasoningKey = dispatch(getKeyDatabase());
      reasoningMessageKeyRef.current = reasoningKey;
      nextMessages[reasoningKey] = {
        message: "",
        messageType: "system",
        userID: "bot",
        isReasoning: true,
        reasoningMessageKeys: [CHAT_THINKING],
        creationTime: new Date().getTime(),
      };

      generateBotMessage(nextMessages, reasoningKey);
      return nextMessages;
    });

    return true;
  };

  const triggerHiddenUserTurn = async (userPrompt) => {
    if (disabledInteractions || isTyping) {
      return false;
    }

    if (!userPrompt || typeof userPrompt !== "string" || !userPrompt.trim()) {
      return false;
    }

    setQuickReplies([]);
    setIsLoadingQuickReplies(false);

    const reasoningKey = dispatch(getKeyDatabase());
    reasoningMessageKeyRef.current = reasoningKey;

    setMessages((prevMessages) => {
      const nextMessages = {
        ...prevMessages,
        [reasoningKey]: {
          message: "",
          messageType: "system",
          userID: "bot",
          isReasoning: true,
          reasoningMessageKeys: [CHAT_CAMPAIGN_OPEN_ANALYZING_SUGGESTIONS],
          creationTime: new Date().getTime(),
        },
      };

      generateBotMessage(nextMessages, reasoningKey, userPrompt.trim());
      return nextMessages;
    });

    return true;
  };

  const isOpen = typeof controlledOpen === "boolean" ? controlledOpen : open;
  const handleOpen = () => {
    if (typeof controlledOpen === "boolean") {
      onChangeOpen && onChangeOpen(true);
    } else {
      setOpen(true);
    }
    onChangeShowChat(true);
  };
  const handleClose = () => {
    if (typeof controlledOpen === "boolean") {
      onChangeOpen && onChangeOpen(false);
    } else {
      setOpen(false);
    }
    onChangeShowChat(false);
  };

  // Welcome cards use i18n keys
  const onWelcomeCardClick = (questionKey) => {
    if (disabledInteractions) {
      return;
    }
    onSendMessage(t(questionKey));
  };

  // Dynamic quick replies already provide the final message
  const onQuickReplyClick = (quickReplyMessage) => {
    if (disabledInteractions) {
      return;
    }
    onSendMessage(quickReplyMessage);
  };

  const generateQuickReplies = async ({
    conversationMessages = [],
    optimizations = [],
  } = {}) => {
    if (
      !Array.isArray(conversationMessages) ||
      conversationMessages.length === 0
    ) {
      return;
    }

    setIsLoadingQuickReplies(true);
    try {
      const response = await dispatch(
        startGenerateQuickReplies({
          messages: conversationMessages,
          optimizations,
          limit: 3,
        })
      );

      if (response?.ok) {
        setQuickReplies(response.data?.replies || []);
        return;
      }

      setQuickReplies([]);
    } finally {
      setIsLoadingQuickReplies(false);
    }
  };

  const generateBotMessage = async (
    messagesSnapshot,
    reasoningKey,
    ephemeralUserTurn = null
  ) => {
    const messagesFormatted = formatMessagesForChatAgent(
      messagesSnapshot,
      userID,
      ephemeralUserTurn
    );

    if (demoMode) {
      const lastUserMessage = messagesFormatted
        .filter((m) => m.role === "user")
        .pop()?.content;
      const replyText =
        typeof demoBotReply === "function"
          ? demoBotReply(lastUserMessage || ephemeralUserTurn || "")
          : demoBotReply || t(ERROR_OCCURRED);

      setTimeout(() => {
        const newMessageBot = {
          message: replyText,
          learnings: [],
          optimizations: [],
          messageType: "text",
          userID: "bot",
          creationTime: new Date().getTime(),
          completion: JSON.stringify({
            answer: replyText,
            optimizations: [],
            learnings: [],
          }),
        };

        setMessages((prevMessages) => {
          if (reasoningKey && prevMessages[reasoningKey]) {
            return {
              ...prevMessages,
              [reasoningKey]: newMessageBot,
            };
          }
          return prevMessages;
        });
        setIsTyping(false);
      }, 600);
      return;
    }

    // We now show an inline reasoning placeholder instead of typingIndicator
    const isOwnerFlow = context === OWNER_ROLE;

    const sendToAgent = async () => {
      // Owner flow: endpoint devuelve { ok, data: { messages: [...] } }
      if (isOwnerFlow) {
        return await dispatch(
          startSendMessageOwnerHelpChatAgent({ messages: messagesFormatted })
        );
      }

      if (isGlobalReportFlow) {
        return await dispatch(
          startSendGlobalAgentMessage({
            messages: messagesFormatted,
            globalReportContext,
          })
        );
      }

      // Normal flow: endpoint devuelve { ok, data: Array<{answer, optimizations, learnings}> }
      return await dispatch(
        startSendMessageChatBot({
          messages: messagesFormatted,
          chatConfigurationID,
          chatContext,
          from: embedded ? MAP_CHAT_FROM.campaign : MAP_CHAT_FROM.report,
        })
      );
    };

    const normalizeAgentResponse = (agentResponse) => {
      if (!agentResponse?.ok) return [];

      if (!isOwnerFlow) {
        return Array.isArray(agentResponse.data) ? agentResponse.data : [];
      }

      const answer = agentResponse.data?.answer ?? [];

      return [
        {
          answer,
          optimizations: [],
          learnings: [],
        },
      ];
    };

    const response = await sendToAgent();

    if (response?.ok) {
      const newMessages = normalizeAgentResponse(response);
      const conversationForQuickReplies = [
        ...messagesFormatted,
        ...newMessages.map((message) => ({
          role: "assistant",
          content: message?.answer || "",
        })),
      ].filter(
        (message) =>
          typeof message?.content === "string" && message.content.trim()
      );
      const optimizationsForQuickReplies = newMessages.flatMap(
        (message) => message?.optimizations || []
      );

      let isFirstReplacementDone = false;
      let firstReplyMessageKey = null;
      for (const message of newMessages) {
        const {
          answer = "",
          optimizations = [],
          learnings = [],
          insights = [],
        } = message;

        onCallbackChangeOptimizations((currentOptimizations) => [
          ...currentOptimizations,
          ...optimizations,
        ]);

        const newMessageBot = {
          message: answer,
          learnings,
          insights,
          optimizations,
          messageType: "text",
          userID: "bot",
          creationTime: new Date().getTime(),
          completion: JSON.stringify({
            answer,
            optimizations,
            learnings,
            insights,
          }),
        };

        setMessages((prevMessages) => {
          if (
            !isFirstReplacementDone &&
            reasoningKey &&
            prevMessages[reasoningKey]
          ) {
            isFirstReplacementDone = true;
            firstReplyMessageKey = reasoningKey;
            return {
              ...prevMessages,
              [reasoningKey]: newMessageBot,
            };
          }
          const key = dispatch(getKeyDatabase());
          return {
            ...prevMessages,
            [key]: newMessageBot,
          };
        });
      }

      if (firstReplyMessageKey) {
        onAgentReply?.({
          messageKey: firstReplyMessageKey,
          isFirstBotMessageInTurn: true,
        });
      }

      if (firstReplyMessageKey) {
        onAgentReply?.({
          messageKey: firstReplyMessageKey,
          isFirstBotMessageInTurn: true,
        });
      }

      if (!isGlobalReportFlow) {
        await generateQuickReplies({
          conversationMessages: conversationForQuickReplies,
          optimizations: optimizationsForQuickReplies,
        });
      } else {
        setQuickReplies([]);
      }
    } else {
      setQuickReplies([]);
      const errorMessage = resolveChatAgentErrorMessage({
        errorOrResponse: response,
        paymentMode: byokPaymentMode,
        t,
      });
      setMessages((prevMessages) => {
        if (reasoningKey && prevMessages[reasoningKey]) {
          return {
            ...prevMessages,
            [reasoningKey]: {
              message: errorMessage,
              messageType: "text",
              userID: "bot",
              creationTime: new Date().getTime(),
            },
          };
        }
        return prevMessages;
      });
    }
    // Ensure typing indicator remains off (we use inline placeholder)
    setIsTyping(false);
  };

  // Load preloaded messages when initializing the component
  useEffect(() => {
    if (preloadedMessages && Object.keys(preloadedMessages).length > 0) {
      const sanitized = Object.fromEntries(
        Object.entries(preloadedMessages).filter(
          ([, msg]) => !msg?.suppressInChatList
        )
      );
      setMessages(sanitized);

      // Extract optimizations from preloaded messages
      Object.values(sanitized).forEach((message) => {
        if (message.optimizations && message.optimizations.length > 0) {
          onCallbackChangeOptimizations((currentOptimizations) => {
            const newOptimizations = message.optimizations.filter(
              (newOpt) =>
                !currentOptimizations.some((opt) => opt.id === newOpt.id)
            );
            return [...currentOptimizations, ...newOptimizations];
          });
        }
      });
    }
  }, [preloadedMessages, onCallbackChangeOptimizations]);

  useEffect(() => {
    const element = window.document.querySelector(".scrollbar-container");
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  }, [messagesKeys?.length, isTyping]);

  // Exponer handler de envío al padre si se requiere — cuando `disabledInteractions` o `isTyping`
  // cambian, el handler debe reexpuesto o el padre puede quedar con un closure que rechaza todos los sends
  // (SlideChat tras `isSyncingData`).
  useEffect(() => {
    if (typeof exposeSendMessage === "function") {
      exposeSendMessage(onSendMessage);
    }
    if (typeof exposeChatActions === "function") {
      exposeChatActions({
        sendMessage: onSendMessage,
        triggerHiddenUserTurn,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers se reconstruyen cada render; estos flags invalidan cuándo hace falta publicar al padre
  }, [exposeSendMessage, exposeChatActions, disabledInteractions, isTyping]);

  // Exponer conteo de mensajes al padre si se requiere
  useEffect(() => {
    if (typeof exposeMessageCount === "function") {
      const messageCount = Object.keys(messages).length;
      exposeMessageCount(messageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, exposeMessageCount]);

  const clearPreloadedMessages = () => {
    setMessages((prevMessages) => {
      const next = {};
      Object.entries(prevMessages).forEach(([key, message]) => {
        if (!message?.isPreloaded) {
          next[key] = message;
        }
      });
      return next;
    });
  };

  // Register clear function for parent (e.g. when campaign is published/saved)
  useEffect(() => {
    onRegisterClearPreloadedMessages(clearPreloadedMessages);
    return () => onRegisterClearPreloadedMessages(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (demoMode) return;
    dispatch(startGetAdsCampaignsSchemas());
    if (!embedded || isInStatisticsPage) return;
    const optimizationsByPlatform = chatContext?.campaign?.report?.states || {};

    if (_.isEmpty(optimizationsByPlatform)) return;

    let allOptimizationsApplied = true;
    Object.entries(optimizationsByPlatform).forEach(([platform, data]) => {
      const key = dispatch(getKeyDatabase());
      const { answer = "", optimizations = [] } = data;
      onCallbackChangeOptimizations((currentOptimizations) => [
        ...currentOptimizations,
        ...optimizations,
      ]);
      const isOptimizationsApplied = optimizations.every(
        (optimization) => optimization.isApplied
      );
      if (!isOptimizationsApplied) {
        allOptimizationsApplied = false;
        setMessages((prevMessages) => ({
          ...prevMessages,
          [key]: {
            platform,
            message: answer,
            optimizations,
            messageType: "text",
            userID: "bot",
            isPreloaded: true,
            creationTime: new Date().getTime(),
            completion: JSON.stringify({
              answer,
              optimizations,
            }),
          },
        }));
      }
    });

    if (!allOptimizationsApplied) {
      handleOpen();
    }

    // eslint-disable-next-line
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <Box
      sx={{
        position: sx?.position || "fixed",
        zIndex: 1299,
        width: embedded
          ? { xs: "100%", md: `calc(100% - ${DRAWER_CAMPAIGN}px)` }
          : 500,
        boxShadow: embedded ? BOX_SHADOW_CHAT : 4,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${INPUT_COLOR_BORDER}`,
        borderLeft: embedded ? "none" : `1px solid ${INPUT_COLOR_BORDER}`,
        backgroundColor: "#fff",
        bottom:
          typeof sx?.bottom !== "undefined"
            ? sx.bottom
            : embedded
              ? "88px"
              : statisticsView
                ? 32
                : 100,
        right:
          typeof sx?.right !== "undefined"
            ? sx.right
            : embedded && !statisticsView
              ? "auto"
              : statisticsView
                ? 16
                : 40,
        borderTopLeftRadius: "12px !important",
        borderTopRightRadius: "12px !important",
        borderBottomLeftRadius: embedded ? "0px !important" : "12px !important",
        borderBottomRightRadius: embedded
          ? "0px !important"
          : "12px !important",
        ...sx,
      }}
    >
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            backgroundColor: "#FFF",
            mb: "0.1px", //litte margin to separate the chat from the container
            borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.palette.button.primary.main,
                color: theme.palette.button.primary.contrastText,
              }}
            >
              <Lightbulb className="w-4 h-4 text-white" />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                pl: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ color: "white", fontWeight: 600, lineHeight: 1 }}
              >
                {t(CHAT_BOT_NAME)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "white", opacity: 0.8, lineHeight: 1 }}
              >
                {t(CHAT_BOT_DESCRIPTION)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={async () => {
                setMessages({});
                if (chatContext?.campaign?.id) {
                  const { userID: advertiserID } = dispatch(
                    getPathDatabaseByRole()
                  );
                  if (advertiserID) {
                    await dispatch(
                      startDeleteCampaignReport({
                        campaignID: chatContext.campaign.id,
                      })
                    );
                  }
                }
              }}
              sx={{ color: "white" }}
              title="Resetear chat"
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {showMinimize && (
              <IconButton size="small" onClick={handleClose}>
                <MinimizeIcon sx={{ color: "white", mb: 1.7 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight:
            typeof contentSx?.minHeight !== "undefined"
              ? contentSx.minHeight
              : embedded
                ? "40vh"
                : 350,
          maxHeight:
            typeof contentSx?.maxHeight !== "undefined"
              ? contentSx.maxHeight
              : embedded
                ? "40vh"
                : 500,
          background: "#fff",
          display: "flex",
          flexDirection: "column",
          padding: 0,
          width: contentSx?.width || "auto",
          height: contentSx?.height || "auto",
        }}
      >
        {!hasAnyMessages ? (
          isGlobalReportFlow ? (
            <Box sx={{ flex: 1, minHeight: 0 }} />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: welcomeCenteredSingleColumn ? "center" : "stretch",
                justifyContent: "center",
                padding: 2,
                textAlign: "center",
                position: "relative",
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <ChatWelcomeDescription
                campaign={chatContext?.campaign}
                onClickCard={onWelcomeCardClick}
                singleColumn={welcomeCenteredSingleColumn}
                agentContext={chatContext?.agentContext}
              />
            </Box>
          )
        ) : (
          <Box sx={chatMessagesRootSx}>
            <Box sx={chatMessagesScrollSx}>
              <ChatContainerLibrary style={CHAT_LIBRARY_INLINE_STYLE}>
                <MessageList style={messageListStyle}>
                  {sortedDisplayMessages.map((messageID, visibleIndex) => {
                    const message = messagesForDisplay[messageID];
                    const messageIndexInFullList =
                      displayMessageIndexById.get(messageID) ?? visibleIndex;
                    const isLastMessage =
                      visibleIndex === sortedDisplayMessages.length - 1;
                    const getDirection = () =>
                      message.userID === userID ? "outgoing" : "incoming";
                    const direction = getDirection();
                    const getImageURL = () => allCurrentChatData.imageURL;
                    return (
                      <Message
                        key={messageID}
                        model={{
                          message: message.message,
                          sender: allCurrentChatData.name,
                          direction: direction,
                          type: "html",
                        }}
                        style={{
                          padding: 0,
                          marginBottom:
                            showQuickRepliesChatOverlay &&
                            !isWaitingForBotResponse &&
                            isLastMessage
                              ? theme.spacing(5)
                              : 10,
                          maxWidth:
                            message?.optimizations?.length > 0 ? "100%" : "85%",
                        }}
                      >
                        {direction === "incoming" && showAvatar && (
                          <Avatar
                            src={getImageURL()}
                            name={allCurrentChatData.name}
                          />
                        )}
                        <Message.CustomContent
                          style={{
                            background: "transparent !important",
                          }}
                        >
                          <ChatCustomMessage
                            message={message}
                            messageKey={messageID}
                            direction={direction}
                            embedded={embedded}
                            messageVariant={messageVariant}
                            optimizationsSingleColumn={
                              optimizationsSingleColumn
                            }
                            onChangeStatusOptimization={(params) =>
                              onChangeStatusOptimization({
                                ...params,
                                messageIndex: messageIndexInFullList,
                              })
                            }
                            showPreviewOptimization={showPreviewOptimization}
                            disabledInteractions={disabledInteractions}
                          />
                        </Message.CustomContent>
                      </Message>
                    );
                  })}
                </MessageList>
              </ChatContainerLibrary>
            </Box>
            {showQuickRepliesChatOverlay &&
              !isWaitingForBotResponse &&
              !disabledInteractions &&
              hasAnyMessages && (
                <Box sx={SX_CHAT_QUICK_REPLIES_OVERLAY}>
                  <Box sx={{ pointerEvents: "auto" }}>
                    <QuickRepliesChips
                      onClickChip={onQuickReplyClick}
                      campaign={chatContext?.campaign}
                      agentContext={chatContext?.agentContext}
                      replies={quickReplies}
                      isLoading={isLoadingQuickReplies}
                    />
                  </Box>
                </Box>
              )}
          </Box>
        )}

        {!showOnlyMessages && (
          <Box
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <CommonChatMessageInput
              as="MessageInput"
              disabled={isTyping || disabledInteractions}
              onSendMessage={onSendMessage}
              embedded={embedded}
              showSendFileButton={showSendFileButton}
              placeholderInput={placeholderInput}
              messageVariant={messageVariant}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CommonChatContainer;
